/** One weakness row: slot · reason · View N targets (PV10, SE-8337). */
import React, { memo } from "react";

import { Button } from "@/components/atoms/Button";
import { Stack } from "@/components/atoms/Stack";
import { Text } from "@/components/atoms/Text";
import type { WeakestPositionRow } from "@/features/scouting/hooks/useThreeWeakestPositions";

/** Prepared props for the row presentation. */
export interface WatchlistThreeWeakestRowViewProps {
  readonly row: WeakestPositionRow;
  /** "View N targets" or the Add-player copy, prepared by the container. */
  readonly label: string;
  /** Decided by the container: an empty pool with no Add-player entry. */
  readonly isDisabled: boolean;
  readonly onPress: () => void;
}

/** The empty-slot row's copy when its pool is empty (SE-8414, frame 944:16362). */
const NO_TARGETS_YET_LABEL = "No targets yet · Add player";

/**
 * Formats the hand-off label; a count still loading is never 0. Only the
 * empty-slot ("No cover") row with nothing in the pool reads the Add-player
 * copy, and only where the surface offers that entry — copy follows the
 * affordance. A covered, still-weak slot with zero targets keeps "View 0
 * targets" (Sprint Review 8 Sep, Jake + Pietra; frame 1009:20290): the
 * discriminator is the row's cover, never the count alone.
 * @param row - The weakness row: its kind and pool count.
 * @param row.kind - Why the slot ranks where it does.
 * @param row.targetCount - Pool count, or null while unknown.
 * @param isAddPlayer - Whether this row is the surface's Add-player entry.
 * @returns Button label.
 */
export const targetsLabel = (
  { kind, targetCount }: Pick<WeakestPositionRow, "kind" | "targetCount">,
  isAddPlayer = false
): string =>
  isAddPlayer && kind === "no-cover" && targetCount === 0
    ? NO_TARGETS_YET_LABEL
    : `View ${targetCount === null ? "…" : targetCount} targets`;

/**
 * Reason text with the unrated note the DR requires.
 * @param row - Weakness row.
 * @returns Reason plus "· n unrated" when eligible players are unrated.
 */
export const reasonText = (row: WeakestPositionRow): string =>
  row.unrated > 0 ? `${row.label} · ${row.unrated} unrated` : row.label;

/**
 * Renders the slot code, the reason and the View N targets hand-off.
 * @param props - Row and bound hand-off.
 * @param props.row - Weakness row.
 * @param props.label - Prepared hand-off label.
 * @param props.isDisabled - Whether the hand-off is inert.
 * @param props.onPress - Opens this slot's targets.
 */
const WatchlistThreeWeakestRowView = ({
  row,
  label,
  isDisabled,
  onPress,
}: WatchlistThreeWeakestRowViewProps) => (
  <Stack
    testID={`watchlist-three-weakest-row-${row.slot}`}
    direction="horizontal"
    className="items-center gap-2 border-b border-outline-default py-3"
  >
    <Text variant="caption-strong" className="w-7 text-content-primary">
      {row.slot}
    </Text>
    <Text
      variant="caption"
      className="min-w-0 flex-1 text-content-muted"
      numberOfLines={2}
    >
      {reasonText(row)}
    </Text>
    <Button
      testID={`watchlist-three-weakest-targets-${row.slot}`}
      label={label}
      accessibilityLabel={`${label} for ${row.slot}`}
      // A text link, as the frame draws it (969:19077). `tone="accent"` on
      // the link variant paints the full accent fill — see the Button atom's
      // TONE_TO_CLASS note; the audit of 2026-09-19 measured exactly that slab.
      variant="link"
      labelTone="accent"
      // SE-8486 (W30): the label names its own text bundle — the vendored
      // button text carries a weight but no family, so the link fell back to
      // the system font — and the box collapses to the 16 px line instead of
      // the xs button's 32 px, which is what made the row 57 tall (W27).
      labelVariant="caption-strong"
      size="xs"
      className="h-auto"
      isDisabled={isDisabled}
      onPress={onPress}
    />
  </Stack>
);

WatchlistThreeWeakestRowView.displayName = "WatchlistThreeWeakestRowView";
export default memo(WatchlistThreeWeakestRowView);
